<template>
  <div :class="$style.container">
    <div :class="$style.paper">
      <!-- 헤더 -->
      <div :class="$style.header">
        <button :class="$style.backButton" @click="goBack">
          ← <span :class="$style.backButtonText">뒤로가기</span>
        </button>
        <h1 :class="$style.title">{{ isEditMode ? "글 수정" : "글쓰기" }}</h1>
        <button :class="$style.publishButton" @click="publishPost">
          {{ isEditMode ? "수정 완료" : "발행" }}
        </button>
      </div>

      <!-- 카테고리 선택 -->
      <div :class="$style.categorySection">
        <div :class="$style.categoryLabel">
          카테고리
          <span v-if="userStore.auth === 'admin' && isNotice" :class="$style.optionalBadge">(공지사항은 선택사항)</span>
        </div>
        <div ref="dropdownContainer" :class="$style.dropdownContainer" @click.stop>
          <div :class="[$style.customDropdown, { [$style.open]: showCategoryDropdown }]"
            @click="toggleCategoryDropdown">
            <span :class="$style.dropdownSelected">
              {{ selectedCategoryText || '카테고리를 선택하세요' }}
            </span>
            <span :class="[$style.dropdownArrow, { [$style.rotated]: showCategoryDropdown }]">▼</span>
          </div>
          <div v-if="showCategoryDropdown" :class="$style.dropdownMenu">
            <div v-for="option in categoryOptions" :key="option.value"
              :class="[$style.dropdownOption, { [$style.selected]: selectedCategory === option.value }]"
              @click="selectCategory(option.value)">
              {{ option.label }}
            </div>
          </div>
        </div>


        <!-- 권한신청 안내 -->
        <div v-if="selectedCategory === 'permission'" :class="$style.permissionNotice">
          <span :class="$style.noticeIcon">🔒</span>
          <span :class="$style.noticeText">
            권한신청은 자동으로 비밀글로 등록되며, 작성자와 관리자만 확인할 수 있습니다.
          </span>
        </div>
        <div v-if="selectedCategory === 'permission'" :class="$style.permissionTypeSelector">
          <button type="button" :class="[
            $style.permissionTypeButton,
            { [$style.activePermissionType]: permissionRequestType === 'artist' },
          ]" @click="selectPermissionRequestType('artist')">
            아티스트 수정권한
          </button>
          <button type="button" :class="[
            $style.permissionTypeButton,
            { [$style.activePermissionType]: permissionRequestType === 'live' },
          ]" @click="selectPermissionRequestType('live')">
            라이브 수정권한
          </button>
        </div>
        <!-- 공연후기 안내 -->
        <div v-if="selectedCategory === 'performance'" :class="$style.performanceNotice">
          <span :class="$style.noticeIcon">✍️</span>
          <span :class="$style.noticeText">
            관람하신 공연에 대한 솔직한 후기를 작성해주세요. 다른 관객들에게 큰 도움이 됩니다!
          </span>
        </div>

        <!-- 버그리포트 안내 -->
        <div v-if="selectedCategory === 'bugreport'" :class="$style.permissionNotice">
          <span :class="$style.noticeIcon">🔒</span>
          <span :class="$style.noticeText">
            버그리포트는 자동으로 비밀글로 등록되며, 작성자와 관리자만 확인할 수 있습니다.
          </span>
        </div>

        <!-- 곡추천: 유튜브 영상 링크 (VideoForm 스타일 미리보기) -->
        <div v-if="selectedCategory === 'songRecommend'" :class="$style.songRecommendVideoSection">
          <div :class="$style.songRecommendVideoLabel">유튜브 영상 링크 <span :class="$style.optionalText">(선택)</span></div>
          <p :class="$style.songRecommendVideoHint">유튜브 영상 URL을 입력하면 제목·채널 등 정보를 미리 볼 수 있습니다.</p>
          <input v-model="songRecommendYoutubeLink" type="url" placeholder="https://www.youtube.com/watch?v=..."
            :class="$style.songRecommendVideoInput" @blur="onSongRecommendLinkBlur" />
          <div v-if="songRecommendLoading" :class="$style.songRecommendVideoLoading">영상 정보를 불러오는 중...</div>
          <div v-else-if="songRecommendVideoPreview" :class="$style.songRecommendVideoPreview">
            <div :class="$style.songRecommendPreviewContent">
              <div :class="$style.songRecommendVideoThumbWrap">
                <img v-if="songRecommendVideoPreview.videoThumbnail" :src="songRecommendVideoPreview.videoThumbnail"
                  :class="$style.songRecommendVideoThumb" alt="영상 썸네일" />
                <span :class="$style.songRecommendPreviewBadge">YouTube</span>
              </div>
              <div :class="$style.songRecommendPreviewInfo">
                <h4 :class="$style.songRecommendPreviewTitle">{{ songRecommendVideoPreview.title }}</h4>
                <div :class="$style.songRecommendPreviewMeta">
                  <div :class="$style.songRecommendChannelPreview">
                    <img v-if="songRecommendVideoPreview.channelThumbnail"
                      :src="songRecommendVideoPreview.channelThumbnail" :class="$style.songRecommendChannelImg"
                      alt="채널 프로필" />
                    <div v-else-if="songRecommendVideoPreview.channel" :class="$style.songRecommendChannelFallback">
                      {{ songRecommendVideoPreview.channel.charAt(0).toUpperCase() }}
                    </div>
                    <span :class="$style.songRecommendChannelName">{{ songRecommendVideoPreview.channel }}</span>
                  </div>
                  <span v-if="songRecommendVideoPreview.publishedAt" :class="$style.songRecommendPreviewDate">
                    {{ formatSongRecommendDate(songRecommendVideoPreview.publishedAt) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 관리자 전용 공지사항 체크박스 -->
        <div v-if="userStore.auth === 'admin'" :class="$style.noticeCheckbox">
          <label :class="$style.checkboxLabel">
            <input type="checkbox" v-model="isNotice" :class="$style.checkbox" />
            <span :class="$style.checkboxText">📢 공지사항으로 등록</span>
          </label>
        </div>

        <!-- 비밀글 체크박스 (권한신청/버그리포트는 자동 비밀글이므로 제외) -->
        <div v-if="selectedCategory !== 'permission' && selectedCategory !== 'bugreport'"
          :class="$style.privateCheckbox">
          <label :class="$style.checkboxLabel">
            <input type="checkbox" v-model="isPrivate" :class="$style.checkbox" />
            <span :class="$style.privateCheckboxText">🔒 비밀글로 등록</span>
          </label>
          <div v-if="isPrivate" :class="$style.privateHint">
            작성자와 관리자만 확인할 수 있습니다.
          </div>
        </div>
      </div>

      <div v-if="selectedCategory === 'ggookoff'" :class="$style.bandOffSection">
        <h3 :class="$style.bandOffTitle">꾹오프 모집 설정</h3>
        <div :class="$style.bandOffRow">
          <label :class="$style.bandOffLabel">오프일 날짜</label>
          <input v-model="bandOffForm.offDate" type="date" :class="$style.bandOffInput" />
        </div>
        <div :class="$style.bandOffRow">
          <label :class="$style.bandOffLabel">모집 상태</label>
          <select v-model="bandOffForm.recruitStatus" :class="$style.bandOffInput">
            <option value="open">모집중</option>
            <option value="closed">마감</option>
          </select>
        </div>
        <div :class="$style.bandOffRow">
          <label :class="$style.bandOffLabel">모집 시작</label>
          <input v-model="bandOffForm.recruitStartAt" type="datetime-local" :class="$style.bandOffInput" />
        </div>
        <div :class="$style.bandOffRow">
          <label :class="$style.bandOffLabel">모집 종료</label>
          <input v-model="bandOffForm.recruitEndAt" type="datetime-local" :class="$style.bandOffInput" />
        </div>
        <div :class="$style.bandOffRow">
          <label :class="$style.bandOffLabel">총 참가 인원 제한</label>
          <input v-model="bandOffForm.maxParticipants" type="number" min="0" placeholder="0이면 제한 없음"
            :class="$style.bandOffInput" />
        </div>

        <h4 :class="$style.bandOffSubtitle">파트별 제한</h4>
        <div :class="$style.partLimitGrid">
          <div v-for="part in bandOffPartOrder" :key="part" :class="$style.partLimitItem">
            <span>{{ part }}</span>
            <input v-model="bandOffForm.partLimits[part]" type="number" min="0" :class="$style.partLimitInput" />
          </div>
        </div>

        <h4 :class="$style.bandOffSubtitle">곡 목록</h4>
        <div v-for="(song, songIndex) in bandOffSongs" :key="songIndex" :class="$style.songItem">
          <input v-model="song.title" type="text" placeholder="곡 제목" :class="$style.songTitleInput" />
          <div :class="$style.songPartSelect">
            <button v-for="part in bandOffPartOrder" :key="`${songIndex}-${part}`" type="button" :class="[
              $style.songPartButton,
              { [$style.songPartButtonActive]: (song.availableParts || []).includes(part) },
            ]" @click="toggleBandOffSongPart(songIndex, part)">
              {{ part }}
            </button>
          </div>
          <button type="button" :class="$style.songRemoveButton" @click="removeBandOffSong(songIndex)">
            곡 삭제
          </button>
        </div>
        <button type="button" :class="$style.songAddButton" @click="addBandOffSong">
          + 곡 추가
        </button>
      </div>

      <!-- 제목 입력 -->
      <div :class="$style.titleSection">
        <input v-model="postTitle" type="text" placeholder="제목을 입력하세요" :class="$style.titleInput" />
      </div>

      <!-- 내용 입력 -->
      <div :class="$style.contentSection">
        <textarea v-model="postContent" placeholder="내용을 입력하세요" :class="$style.contentTextarea"></textarea>
      </div>

      <!-- 이미지 업로드 섹션 -->
      <div :class="$style.imageSection">
        <div :class="$style.imageSectionHeader">
          <span :class="$style.imageSectionTitle">📷 이미지 추가 (최대 4개)</span>
        </div>
        <div :class="$style.imageUploadArea">
          <button v-if="images.length < 4" @click="$refs.fileInput.click()" :class="$style.uploadButton">
            + 이미지 선택
          </button>
          <input ref="fileInput" type="file" accept="image/*" multiple @change="handleImageSelect"
            :class="$style.fileInput" />
        </div>
        <div v-if="images.length > 0" :class="[$style.imagePreviewGrid, $style[`grid${images.length}`]]">
          <div v-for="(image, index) in images" :key="index" :class="$style.imagePreviewItem">
            <img :src="image.preview" :class="$style.previewImage" />
            <button @click="removeImage(index)" :class="$style.removeImageButton">
              ×
            </button>
          </div>
        </div>
      </div>

      <!-- 하단 정보 -->
      <div :class="$style.footer">
        <div :class="$style.wordCount">{{ postContent.length }}자</div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { doc, getDoc } from "firebase/firestore";
import { firestore } from "/firebaseConfig";
import { useUserStore } from "@/store/user";
import { createPost, updatePost } from "@/utils/write/board";
import { uploadBoardImages } from "@/utils/write/storage";
import { createBandOffPostArtifacts, getBandOffSongs } from "@/utils/write/bandOff";
import { getBandOffPartOrder, getDefaultBandOffConfig, normalizeBandOffSongs } from "@/utils/bandOff/schema";

export default {
  name: "WriteBoard",
  data() {
    return {
      selectedCategory: "",
      postTitle: "",
      postContent: "",
      isNotice: false,
      isPrivate: false,
      isEditMode: false,
      editPostId: null,
      images: [], // 업로드된 이미지 데이터
      showCategoryDropdown: false,
      permissionRequestType: "artist", // 권한신청 양식 타입: artist | live
      bandOffPartOrder: getBandOffPartOrder(),
      bandOffForm: {
        offDate: "",
        recruitStatus: "open",
        recruitStartAt: "",
        recruitEndAt: "",
        maxParticipants: "",
        partLimits: {},
      },
      bandOffSongs: [{ title: "", availableParts: [] }],
      allCategoryOptions: [
        { value: "", label: "카테고리를 선택하세요" },
        { value: "notice", label: "📢 공지사항" },
        { value: "permission", label: "🔑 권한신청" },
        { value: "recruit", label: "💼 구인구직" },
        { value: "performance", label: "🎭 공연후기" },
        { value: "recommend", label: "⭐ 추천게시판" },
        { value: "songRecommend", label: "🎵 곡추천" },
        { value: "ggookoff", label: "🎪 꾹오프" },
        { value: "free", label: "💬 자유게시판" },
        { value: "update", label: "🔄 업데이트" },
        { value: "bugreport", label: "🐛 버그리포트" },
      ],
      // 곡추천 전용: 유튜브 링크 및 미리보기
      songRecommendYoutubeLink: "",
      songRecommendVideoPreview: null,
      songRecommendLoading: false,
    };
  },
  computed: {
    userStore() {
      return useUserStore();
    },
    isAdmin() {
      return this.userStore.auth === "admin";
    },
    categoryOptions() {
      // 관리자 전용 카테고리
      const adminOnly = new Set(["update", "notice", "ggookoff"]);
      return this.allCategoryOptions.filter((option) => !adminOnly.has(option.value) || this.isAdmin);
    },
    selectedCategoryText() {
      const option = this.categoryOptions.find(opt => opt.value === this.selectedCategory);
      return option ? option.label : '';
    },
  },
  async mounted() {
    // 외부 클릭 시 드롭다운 닫기
    document.addEventListener('click', this.closeCategoryDropdown);
    this.resetBandOffForm();

    // 수정 모드인지 확인
    const editId = this.$route.query.editId;
    if (editId) {
      await this.loadPostForEdit(editId);
    } else {
      // URL 쿼리 파라미터에서 카테고리와 아티스트명/공연제목 확인
      if (this.$route.query.category) {
        this.selectedCategory = this.$route.query.category;
        // '업데이트', '공지사항'은 관리자만 선택 가능
        if (!this.isAdmin && (this.selectedCategory === "update" || this.selectedCategory === "notice" || this.selectedCategory === "ggookoff")) {
          this.selectedCategory = "";
        }
        // 권한신청/버그리포트 카테고리면 비밀글 자동 설정
        if (this.selectedCategory === 'permission' || this.selectedCategory === 'bugreport') {
          this.isPrivate = true;
        }
        if (this.selectedCategory === "ggookoff") {
          this.resetBandOffForm();
        }
      }
      if (this.selectedCategory === 'permission' && this.$route.query.liveTitle) {
        this.permissionRequestType = "live";
        this.applyPermissionFormTemplate();
      } else if (this.$route.query.artistName) {
        this.permissionRequestType = "artist";
        this.applyPermissionFormTemplate();
      } else if (this.selectedCategory === 'permission' && !this.postTitle && !this.postContent) {
        this.permissionRequestType = "artist";
        this.applyPermissionFormTemplate();
      }
      if (this.selectedCategory === 'performance' && this.$route.query.liveTitle) {
        const liveTitle = this.$route.query.liveTitle;
        this.fillPerformanceReviewForm(liveTitle);
      } else if (this.selectedCategory === 'performance' && !this.postTitle && !this.postContent) {
        // 공연후기 카테고리가 선택되었지만 공연제목이 없는 경우
        this.fillPerformanceReviewForm();
      }
    }
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeCategoryDropdown);
  },
  watch: {
    selectedCategory(newCategory, oldCategory) {
      // '업데이트', '공지사항'은 관리자만 선택 가능 (우회 방지)
      if (!this.isAdmin && (newCategory === "update" || newCategory === "notice" || newCategory === "ggookoff")) {
        this.selectedCategory = "";
        return;
      }
      // 곡추천에서 다른 카테고리로 변경 시 유튜브 링크/미리보기 초기화
      if (newCategory !== "songRecommend") {
        this.songRecommendYoutubeLink = "";
        this.songRecommendVideoPreview = null;
      }
      // 권한신청에서 다른 카테고리로 변경될 때 폼 초기화
      if (oldCategory === 'permission' && newCategory !== 'permission' && !this.isEditMode) {
        this.postTitle = '';
        this.postContent = '';
        this.isPrivate = false;
      }
      // 버그리포트에서 다른 카테고리로 변경될 때 비밀글 설정 해제
      if (oldCategory === 'bugreport' && newCategory !== 'bugreport' && !this.isEditMode) {
        this.isPrivate = false;
      }
      // 공연후기에서 다른 카테고리로 변경될 때 폼 초기화
      if (oldCategory === 'performance' && newCategory !== 'performance' && !this.isEditMode) {
        this.postTitle = '';
        this.postContent = '';
      }
      if (newCategory === "ggookoff" && !this.isEditMode) {
        this.resetBandOffForm();
      }
      // 권한신청 카테고리를 선택했을 때
      if (newCategory === 'permission' && !this.isEditMode) {
        this.isPrivate = true;
        this.permissionRequestType = this.$route.query.liveTitle ? "live" : "artist";
        if (!this.postTitle && !this.postContent) {
          this.applyPermissionFormTemplate();
        }
      }
      // 버그리포트 카테고리를 선택했을 때 비밀글 자동 설정
      if (newCategory === 'bugreport' && !this.isEditMode) {
        this.isPrivate = true;
      }
      // 공연후기 카테고리를 선택했을 때, 제목과 내용이 비어있으면 양식 자동 채우기
      if (newCategory === 'performance' && !this.postTitle && !this.postContent && !this.isEditMode) {
        this.fillPerformanceReviewForm();
      }
    },
    permissionRequestType() {
      if (this.selectedCategory !== "permission" || this.isEditMode) return;
      this.postTitle = "";
      this.postContent = "";
      this.applyPermissionFormTemplate();
    },
  },
  methods: {
    getEmptyBandOffForm() {
      const defaults = getDefaultBandOffConfig();
      return {
        offDate: "",
        recruitStatus: defaults.recruitStatus,
        recruitStartAt: "",
        recruitEndAt: "",
        maxParticipants: "",
        partLimits: { ...defaults.partLimits },
      };
    },
    resetBandOffForm() {
      this.bandOffForm = this.getEmptyBandOffForm();
      this.bandOffSongs = [{ title: "", availableParts: [] }];
    },
    formatDateForInput(dateValue) {
      if (!dateValue) return "";
      const date = dateValue?.toDate ? dateValue.toDate() : new Date(dateValue);
      if (Number.isNaN(date.getTime())) return "";
      const pad = (value) => String(value).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
        date.getHours()
      )}:${pad(date.getMinutes())}`;
    },
    formatDateOnlyForInput(dateValue) {
      if (!dateValue) return "";
      const date = dateValue?.toDate ? dateValue.toDate() : new Date(dateValue);
      if (Number.isNaN(date.getTime())) return "";
      const pad = (value) => String(value).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    },
    hydrateBandOffForm(postData, songs = []) {
      const defaults = this.getEmptyBandOffForm();
      const config = postData.bandOffConfig || {};
      this.bandOffForm = {
        offDate: this.formatDateOnlyForInput(config.offDate),
        recruitStatus: config.recruitStatus === "closed" ? "closed" : "open",
        recruitStartAt: this.formatDateForInput(config.recruitStartAt),
        recruitEndAt: this.formatDateForInput(config.recruitEndAt),
        maxParticipants: config.maxParticipants || "",
        partLimits: {
          ...defaults.partLimits,
          ...(config.partLimits || {}),
        },
      };
      this.bandOffSongs = songs.length
        ? songs.map((song) => ({
          id: song.id,
          title: song.title || "",
          availableParts: Array.isArray(song.availableParts) ? song.availableParts : [],
        }))
        : [{ title: "", availableParts: [] }];
    },
    addBandOffSong() {
      this.bandOffSongs.push({ title: "", availableParts: [] });
    },
    removeBandOffSong(index) {
      this.bandOffSongs.splice(index, 1);
      if (this.bandOffSongs.length === 0) {
        this.bandOffSongs = [{ title: "", availableParts: [] }];
      }
    },
    toggleBandOffSongPart(songIndex, part) {
      const currentParts = this.bandOffSongs[songIndex].availableParts || [];
      this.bandOffSongs[songIndex].availableParts = currentParts.includes(part)
        ? currentParts.filter((item) => item !== part)
        : [...currentParts, part];
    },
    buildBandOffPayload() {
      const maxParticipants = Number.parseInt(this.bandOffForm.maxParticipants, 10);
      const partLimits = {};
      this.bandOffPartOrder.forEach((part) => {
        const rawLimit = Number.parseInt(this.bandOffForm.partLimits[part], 10);
        partLimits[part] = Number.isFinite(rawLimit) && rawLimit >= 0 ? rawLimit : 0;
      });

      const songs = normalizeBandOffSongs(
        this.bandOffSongs.map((song, index) => ({
          title: song.title,
          availableParts: song.availableParts,
          order: index + 1,
          active: true,
        }))
      );
      if (songs.length === 0) {
        throw new Error("꾹오프 곡을 1개 이상 입력해주세요.");
      }

      return {
        config: {
          offDate: this.bandOffForm.offDate || null,
          recruitStatus: this.bandOffForm.recruitStatus === "closed" ? "closed" : "open",
          recruitStartAt: this.bandOffForm.recruitStartAt || null,
          recruitEndAt: this.bandOffForm.recruitEndAt || null,
          maxParticipants: Number.isFinite(maxParticipants) && maxParticipants >= 0 ? maxParticipants : 0,
          partLimits,
        },
        songs,
      };
    },
    toggleCategoryDropdown() {
      this.showCategoryDropdown = !this.showCategoryDropdown;
    },
    selectCategory(value) {
      this.selectedCategory = value;
      this.showCategoryDropdown = false;
    },
    closeCategoryDropdown(event) {
      // 드롭다운 컨테이너 내부 클릭인지 확인
      if (!event || !event.target) {
        this.showCategoryDropdown = false;
        return;
      }
      const dropdownContainer = this.$refs.dropdownContainer;
      if (dropdownContainer && dropdownContainer.contains(event.target)) {
        return;
      }
      this.showCategoryDropdown = false;
    },
    extractYouTubeVideoId(url) {
      if (!url || typeof url !== "string") return null;
      const u = url.trim();
      if (u.includes("youtu.be/")) return u.split("youtu.be/")[1].split(/[?&]/)[0];
      if (u.includes("youtube.com/watch?v=")) return u.split("v=")[1].split(/[?&]/)[0];
      return null;
    },
    async onSongRecommendLinkBlur() {
      const link = (this.songRecommendYoutubeLink || "").trim();
      if (!link) {
        this.songRecommendVideoPreview = null;
        return;
      }
      const vid = this.extractYouTubeVideoId(link);
      if (!vid) {
        this.songRecommendVideoPreview = null;
        return;
      }
      this.songRecommendLoading = true;
      this.songRecommendVideoPreview = null;
      try {
        const apiUrl = `https://www.googleapis.com/youtube/v3/videos?part=snippet&id=${vid}&key=${process.env.VUE_APP_YOUTUBE_API_KEY}`;
        const response = await axios.get(apiUrl);
        const items = response.data?.items || [];
        if (items.length > 0) {
          const s = items[0].snippet;
          const thumb = s.thumbnails?.maxres?.url || s.thumbnails?.standard?.url || s.thumbnails?.high?.url || s.thumbnails?.medium?.url || s.thumbnails?.default?.url || null;
          const title = s.title || "";
          const channelId = s.channelId || null;
          let channelThumbnail = null;
          if (channelId) {
            try {
              const channelRes = await axios.get(
                `https://www.googleapis.com/youtube/v3/channels?part=snippet&id=${channelId}&key=${process.env.VUE_APP_YOUTUBE_API_KEY}`
              );
              const chItems = channelRes.data?.items || [];
              if (chItems.length > 0) {
                const chThumbs = chItems[0].snippet?.thumbnails;
                channelThumbnail = chThumbs?.high?.url || chThumbs?.medium?.url || chThumbs?.default?.url || null;
              }
            } catch (chErr) {
              console.warn("곡추천 채널 썸네일 조회 실패:", chErr);
            }
          }
          this.songRecommendVideoPreview = {
            videoId: vid,
            title,
            channel: s.channelTitle || "",
            channelThumbnail,
            videoThumbnail: thumb,
            publishedAt: s.publishedAt || null,
          };
          // 제목만 자동 채우기 (앞에 [곡추천] 붙임)
          if (title) this.postTitle = `[곡추천] ${title}`;
        }
      } catch (err) {
        console.warn("곡추천 유튜브 정보 조회 실패:", err);
        this.songRecommendVideoPreview = null;
      } finally {
        this.songRecommendLoading = false;
      }
    },
    formatSongRecommendDate(publishedAt) {
      if (!publishedAt) return "";
      const d = new Date(publishedAt);
      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      const day = d.getDate();
      return `${y}년 ${m}월 ${day}일`;
    },
    selectPermissionRequestType(type) {
      this.permissionRequestType = type;
    },
    applyPermissionFormTemplate() {
      if (this.permissionRequestType === "live") {
        const liveTitle = this.$route.query.liveTitle || null;
        this.fillLivePermissionForm(liveTitle);
        return;
      }
      const artistName = this.$route.query.artistName || null;
      this.fillPermissionForm(artistName);
    },
    fillPermissionForm(artistName = null) {
      // artistName이 없으면 사용자 닉네임 사용
      const targetArtistName = artistName || this.userStore.nickname || '';

      if (targetArtistName) {
        this.postTitle = `[${targetArtistName}] 아티스트 카드 수정 권한 신청`;
        this.postContent = `■ 신청 아티스트: ${targetArtistName}
        

■ 자기소개
(본인이 해당 아티스트임을 증명할 수 있는 소개를 작성해주세요)


■ 인증 이미지
(활동사진, SNS 계정주 인증 등을 이미지로 첨부해주세요)


■ 링꾹라이브 사용자 email 주소
${this.userStore.email || "(로그인된 이메일 주소를 입력해주세요)"}


■ 추가 정보
(필요시 추가 정보를 작성해주세요)
`;
      } else {
        this.postTitle = `아티스트 카드 수정 권한 신청`;
        this.postContent = `■ 신청 아티스트: 
(수정 권한을 신청할 아티스트명을 입력해주세요)

■ 자기소개
(본인이 해당 아티스트임을 증명할 수 있는 소개를 작성해주세요)


■ 인증 이미지
(활동사진, SNS 계정주 인증 등을 이미지로 첨부해주세요)


■ 링꾹라이브 사용자 email 주소
${this.userStore.email || "(로그인된 이메일 주소를 입력해주세요)"}


■ 추가 정보
(필요시 추가 정보를 작성해주세요)
`;
      }
    },
    fillLivePermissionForm(liveTitle = null) {
      if (liveTitle) {
        this.postTitle = `[${liveTitle}] 공연정보 수정 권한 신청`;
        this.postContent = `■ 신청 공연명: ${liveTitle}
        

■ 신청 사유
(해당 공연의 수정 권한이 필요한 이유를 작성해주세요)


■ 신청자 소개
(본인이 해당 공연 정보 수정과 관련된 당사자임을 증명할 수 있는 소개를 작성해주세요)


■ 인증 이미지
(공연 관련 캡처, 공식 SNS/기획 계정 인증 등 증빙 이미지를 첨부해주세요)


■ 링꾹라이브 사용자 email 주소
${this.userStore.email || "(로그인된 이메일 주소를 입력해주세요)"}


■ 수정이 필요한 항목
(예: 날짜/장소/라인업/함께한 사람들/이미지 교체 등 구체적으로 작성해주세요)
`;
      } else {
        this.postTitle = `공연정보 수정 권한 신청`;
        this.postContent = `■ 신청 공연명:
(수정 권한을 신청할 공연명을 입력해주세요)


■ 신청 사유
(해당 공연의 수정 권한이 필요한 이유를 작성해주세요)


■ 신청자 소개
(본인이 해당 공연 정보 수정과 관련된 당사자임을 증명할 수 있는 소개를 작성해주세요)


■ 인증 이미지
(공연 관련 캡처, 공식 SNS/기획 계정 인증 등 증빙 이미지를 첨부해주세요)


■ 링꾹라이브 사용자 email 주소
${this.userStore.email || "(로그인된 이메일 주소를 입력해주세요)"}


■ 수정이 필요한 항목
(예: 날짜/장소/라인업/함께한 사람들/이미지 교체 등 구체적으로 작성해주세요)
`;
      }
    },
    fillPerformanceReviewForm(liveTitle = null) {
      if (liveTitle) {
        this.postTitle = `[${liveTitle}] 공연 후기`;
        this.postContent = `■ 공연명
${liveTitle}


■ 공연 날짜 및 장소
(예: 2024.12.11 @ 롤링홀)


■ 전체적인 느낌 ⭐⭐⭐⭐⭐
(5점 만점으로 평가해주세요)


■ 공연 분위기
(공연장 분위기, 관객 반응 등을 작성해주세요)


■ 가장 인상 깊었던 곡/순간
(기억에 남는 곡이나 순간을 작성해주세요)


■ 추천 여부
(다른 분들께 추천하고 싶은지 의견을 남겨주세요)


■ 기타 후기
(추가로 공유하고 싶은 내용이 있다면 자유롭게 작성해주세요)
`;
      } else {
        this.postTitle = `공연 후기`;
        this.postContent = `■ 공연명
(공연명을 입력해주세요)


■ 공연 날짜 및 장소
(예: 2024.12.11 @ 롤링홀)


■ 전체적인 느낌 ⭐⭐⭐⭐⭐
(5점 만점으로 평가해주세요)


■ 공연 분위기
(공연장 분위기, 관객 반응 등을 작성해주세요)


■ 가장 인상 깊었던 곡/순간
(기억에 남는 곡이나 순간을 작성해주세요)


■ 추천 여부
(다른 분들께 추천하고 싶은지 의견을 남겨주세요)


■ 기타 후기
(추가로 공유하고 싶은 내용이 있다면 자유롭게 작성해주세요)
`;
      }
    },
    goBack() {
      this.$router.go(-1);
    },
    handleImageSelect(event) {
      const files = Array.from(event.target.files);
      const remainingSlots = 4 - this.images.length;

      if (files.length > remainingSlots) {
        this.$alert(
          `최대 4개까지 업로드 가능합니다. (현재 ${this.images.length}개)`
        );
        return;
      }

      files.forEach((file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.images.push({
            file: file,
            preview: e.target.result,
          });
        };
        reader.readAsDataURL(file);
      });

      // input 초기화
      event.target.value = "";
    },
    removeImage(index) {
      this.images.splice(index, 1);
    },
    async uploadImages() {
      const uploadedUrls = [];
      const newImageFiles = [];

      // 기존 이미지와 새 이미지 분리
      for (const image of this.images) {
        if (image.url) {
          // 기존 이미지는 URL 그대로 사용
          uploadedUrls.push(image.url);
        } else {
          // 새 이미지는 파일 배열에 추가
          newImageFiles.push(image.file);
        }
      }

      // 새 이미지가 있으면 업로드
      if (newImageFiles.length > 0) {
        try {
          const newUrls = await uploadBoardImages(newImageFiles);
          uploadedUrls.push(...newUrls);
        } catch (error) {
          console.error("이미지 업로드 오류:", error);
          throw error;
        }
      }

      return uploadedUrls;
    },
    async loadPostForEdit(postId) {
      try {
        const postDoc = await getDoc(doc(firestore, "boards", postId));
        if (!postDoc.exists()) {
          this.$alert("게시글을 찾을 수 없습니다.");
          this.$router.push("/board");
          return;
        }

        const postData = postDoc.data();
        if (postData.category === "ggookoff" && !this.isAdmin) {
          this.$alert("관리자만 수정할 수 있습니다.");
          this.$router.push("/board");
          return;
        }

        // 권한 확인
        const isNoticePost = postData.category === "notice" || postData.isNotice === true;
        const canEdit = isNoticePost
          ? this.isAdmin
          : (this.isAdmin ||
            postData.writer === this.userStore.nickname ||
            postData.writer === this.userStore.email);

        if (!canEdit) {
          this.$alert("수정 권한이 없습니다.");
          this.$router.push("/board");
          return;
        }

        // 데이터 로드
        this.isEditMode = true;
        this.editPostId = postId;
        this.selectedCategory = postData.category;
        this.postTitle = postData.title;
        this.postContent = postData.contents;
        this.isNotice = postData.isNotice || false;
        this.isPrivate = postData.isPrivate || false;

        // 곡추천: 유튜브 링크·미리보기 복원
        if (postData.category === "songRecommend") {
          this.songRecommendYoutubeLink = postData.youtubeLink || "";
          this.songRecommendVideoPreview = postData.youtubeVideoInfo || null;
        }

        // 기존 이미지 로드 (URL을 미리보기로 표시)
        if (postData.images && postData.images.length > 0) {
          this.images = postData.images.map((url) => ({
            file: null, // 이미 업로드된 이미지는 file이 없음
            preview: url,
            url: url, // 기존 URL 저장
          }));
        }

        if (postData.type === "bandOff" || postData.category === "ggookoff") {
          const songs = await getBandOffSongs(postId);
          this.hydrateBandOffForm(postData, songs);
        }
      } catch (error) {
        console.error("게시글 로드 오류:", error);
        this.$alert("게시글을 불러오는 중 오류가 발생했습니다.");
        this.$router.push("/community");
      }
    },
    async publishPost() {
      // 공지사항이 아닐 때만 카테고리 필수
      if (!this.isNotice && !this.selectedCategory) {
        this.$alert("카테고리를 선택해주세요.");
        return;
      }
      // '업데이트', '공지사항'은 관리자만 작성 가능
      if (!this.isAdmin && (this.selectedCategory === "update" || this.selectedCategory === "notice" || this.selectedCategory === "ggookoff" || this.isNotice)) {
        this.$alert("공지사항/업데이트/꾹오프는 관리자만 작성할 수 있습니다.");
        return;
      }
      if (!this.postTitle.trim()) {
        this.$alert("제목을 입력해주세요.");
        return;
      }
      if (!this.postContent.trim()) {
        this.$alert("내용을 입력해주세요.");
        return;
      }

      if (!this.userStore.isLogin) {
        this.$alert("로그인이 필요합니다.");
        return;
      }

      try {
        let bandOffPayload = null;
        if (this.selectedCategory === "ggookoff") {
          bandOffPayload = this.buildBandOffPayload();
        }

        // 이미지 업로드
        let imageUrls = [];
        if (this.images.length > 0) {
          try {
            imageUrls = await this.uploadImages();
          } catch (error) {
            this.$alert("이미지 업로드 중 오류가 발생했습니다.");
            return;
          }
        }

        if (this.isEditMode) {
          // 수정 모드
          const updateData = {
            title: this.postTitle.trim(),
            contents: this.postContent.trim(),
            category: this.selectedCategory,
            ...(this.selectedCategory === "ggookoff"
              ? {
                type: "bandOff",
                bandOffConfig: bandOffPayload.config,
                songs: bandOffPayload.songs,
              }
              : {}),
            ...(this.selectedCategory === "songRecommend"
              ? {
                youtubeLink: this.songRecommendYoutubeLink.trim() || null,
                youtubeVideoInfo: this.songRecommendVideoPreview
                  ? {
                    videoId: this.songRecommendVideoPreview.videoId,
                    title: this.songRecommendVideoPreview.title,
                    channel: this.songRecommendVideoPreview.channel,
                    channelThumbnail: this.songRecommendVideoPreview.channelThumbnail,
                    videoThumbnail: this.songRecommendVideoPreview.videoThumbnail,
                    publishedAt: this.songRecommendVideoPreview.publishedAt,
                  }
                  : null,
              }
              : {}),
            writerAuth: this.userStore.auth || "user",
            isNotice: this.userStore.auth === "admin" ? this.isNotice : false,
            isPrivate: (this.selectedCategory === "permission" || this.selectedCategory === "bugreport") ? true : this.isPrivate,
          };

          // 새로운 이미지가 있으면 추가
          if (imageUrls.length > 0) {
            updateData.images = imageUrls;
          }

          await updatePost(this.editPostId, updateData);
          if (this.selectedCategory === "ggookoff") {
            await createBandOffPostArtifacts(
              this.editPostId,
              bandOffPayload.config,
              bandOffPayload.songs
            );
          }

          this.$alert("게시글이 수정되었습니다!");
          this.$router.push(`/board/${this.editPostId}`);
        } else {
          // 새 게시글 작성
          const postData = {
            title: this.postTitle.trim(),
            contents: this.postContent.trim(),
            category: this.selectedCategory,
            ...(this.selectedCategory === "ggookoff"
              ? {
                type: "bandOff",
                bandOffConfig: bandOffPayload.config,
                songs: bandOffPayload.songs,
              }
              : {}),
            ...(this.selectedCategory === "songRecommend"
              ? {
                youtubeLink: this.songRecommendYoutubeLink.trim() || null,
                youtubeVideoInfo: this.songRecommendVideoPreview
                  ? {
                    videoId: this.songRecommendVideoPreview.videoId,
                    title: this.songRecommendVideoPreview.title,
                    channel: this.songRecommendVideoPreview.channel,
                    channelThumbnail: this.songRecommendVideoPreview.channelThumbnail,
                    videoThumbnail: this.songRecommendVideoPreview.videoThumbnail,
                    publishedAt: this.songRecommendVideoPreview.publishedAt,
                  }
                  : null,
              }
              : {}),
            writer: this.userStore.nickname || this.userStore.email,
            writerEmail: this.userStore.email,
            writerAuth: this.userStore.auth || "user",
            like: 0,
            likedUsers: [],
            isNotice: this.userStore.auth === "admin" ? this.isNotice : false,
            isPrivate: (this.selectedCategory === "permission" || this.selectedCategory === "bugreport") ? true : this.isPrivate,
            images: imageUrls,
          };

          const createdPostId = await createPost(postData);
          if (this.selectedCategory === "ggookoff") {
            await createBandOffPostArtifacts(
              createdPostId,
              bandOffPayload.config,
              bandOffPayload.songs
            );
          }

          const categoryNames = {
            notice: "공지사항",
            performance: "공연후기",
            recruit: "구인구직",
            recommend: "추천게시판",
            songRecommend: "곡추천",
            ggookoff: "꾹오프",
            free: "자유게시판",
            update: "업데이트",
            permission: "권한신청",
            bugreport: "버그리포트",
          };

          const message = this.isNotice
            ? "공지사항이 발행되었습니다!"
            : `${categoryNames[this.selectedCategory]
            }에 게시글이 발행되었습니다!`;

          this.$alert(message);

          this.$router.push("/board");
        }
      } catch (error) {
        console.error("게시글 처리 오류:", error);
        this.$alert(
          `게시글 ${this.isEditMode ? "수정" : "발행"
          } 중 오류가 발생했습니다. 다시 시도해주세요.`
        );
      }
    },
  },
};
</script>

<style module>
/* Medium: 481px ~ (기본 스타일, 최대 1024px) */
.container {
  width: 100%;
  margin: auto;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.paper {
  width: 100%;
  background: white;
  border-radius: 8px;
  padding: 0;
  min-height: calc(100vh - 90px);
  display: flex;
  flex-direction: column;
}

/* 헤더 */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 25px;
  border-bottom: 1px solid #e9ecef;
  background: #fafafa;
  border-radius: 8px 8px 0 0;
}

.backButton {
  background: none;
  border: none;
  color: #666;
  font-size: 15px;
  cursor: pointer;
  padding: 7px 11px;
  border-radius: 6px;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.backButton:hover {
  background: #e9ecef;
  color: #333;
}

.backButtonText {
  font-size: 13px;
  font-family: "NanumSquareRoundR", sans-serif;
}

.title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  font-family: "NanumSquareRoundB", sans-serif;
}

.publishButton {
  background: #66bdff;
  color: white;
  border: none;
  padding: 9px 18px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.publishButton:hover {
  background: #4a9eff;
  transform: translateY(-1px);
}

.publishButton:active {
  transform: translateY(0);
}

/* 카테고리 섹션 */
.categorySection {
  padding: 18px 25px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
}

.categoryLabel {
  font-size: 13px;
  font-weight: 500;
  color: #666;
  margin-bottom: 10px;
  font-family: "NanumSquareRoundR", sans-serif;
}

.dropdownContainer {
  position: relative;
  z-index: 100;
}

.customDropdown {
  width: 100%;
  padding: 11px 15px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #333;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.customDropdown:hover {
  border-color: #66bdff;
  box-shadow: 0 0 0 2px rgba(102, 189, 255, 0.1);
}

.customDropdown.open {
  border-color: #66bdff;
  box-shadow: 0 0 0 3px rgba(102, 189, 255, 0.1);
}

.dropdownSelected {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dropdownArrow {
  flex-shrink: 0;
  font-size: 12px;
  color: #666;
  transition: transform 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dropdownArrow.rotated {
  transform: rotate(180deg);
}

.dropdownMenu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-height: 300px;
  overflow-y: auto;
  z-index: 1001;
  animation: slideDown 0.2s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dropdownOption {
  padding: 11px 15px;
  font-size: 13px;
  color: #333;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
  border-bottom: 1px solid #f0f0f0;
}

.dropdownOption:last-child {
  border-bottom: none;
}

.dropdownOption:hover {
  background: #f8f9fa;
  color: #66bdff;
}

.dropdownOption.selected {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  color: #1976d2;
  font-weight: 600;
}

/* 공지사항 체크박스 */
.noticeCheckbox {
  /* margin-top: 15px; */
  padding: 5px;
  /* background: #fff3cd;
  border: 1px solid #ffeaa7; */
  border-radius: 8px;
}

/* 비밀글 체크박스 */
.privateCheckbox {
  padding: 5px;
  border-radius: 8px;
  margin-top: 8px;
}

.checkboxLabel {
  display: flex;
  align-items: center;
  cursor: pointer;
  margin: 0;
  font-family: "NanumSquareRoundR", sans-serif;
}

.checkbox {
  margin-right: 8px;
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.checkboxText {
  font-size: 13px;
  font-weight: 500;
  color: #856404;
  user-select: none;
}

.checkboxLabel:hover .checkboxText {
  color: #533c02;
}

.privateCheckboxText {
  font-size: 13px;
  font-weight: 500;
  color: #666;
  user-select: none;
}

.checkboxLabel:hover .privateCheckboxText {
  color: #333;
}

.privateHint {
  margin-top: 6px;
  margin-left: 24px;
  font-size: 11px;
  color: #999;
  font-family: "NanumSquareRoundR", sans-serif;
}

.optionalBadge {
  margin-left: 8px;
  font-size: 11px;
  color: #66bdff;
  font-weight: normal;
}

.permissionNotice {
  margin-top: 10px;
  padding: 10px 14px;
  background: #e3f2fd;
  border: 1px solid #2196f3;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.permissionTypeSelector {
  margin-top: 8px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.permissionTypeButton {
  background: #ffffff;
  color: #2b5f8a;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 12px;
  font-weight: 700;
  font-family: "NanumSquareRoundB", sans-serif;
  cursor: pointer;
  transition: none;
  width: 100%;
}

.activePermissionType {
  background: #1f6fd6;
  color: #fff;
}

.performanceNotice {
  margin-top: 10px;
  padding: 10px 14px;
  background: #e6f7ff;
  border: 1px solid #4096ff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
}

/* 곡추천: 유튜브 링크·미리보기 (VideoForm 스타일) */
.songRecommendVideoSection {
  margin-top: 14px;
  padding: 14px 16px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 10px;
}

.songRecommendVideoLabel {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
  font-family: "NanumSquareRoundB", sans-serif;
}

.optionalText {
  font-size: 12px;
  font-weight: 400;
  color: #999;
  margin-left: 2px;
}

.songRecommendVideoHint {
  font-size: 12px;
  color: #66bdff;
  margin: 0 0 10px 0;
  line-height: 1.5;
  font-family: "NanumSquareRoundR", sans-serif;
}

.songRecommendVideoInput {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
  margin-bottom: 10px;
  box-sizing: border-box;
}

.songRecommendVideoInput:focus {
  outline: none;
  border-color: #66bdff;
}

.songRecommendVideoLoading {
  font-size: 13px;
  color: #666;
  padding: 12px 0;
}

.songRecommendVideoPreview {
  margin-top: 10px;
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.songRecommendPreviewContent {
  display: flex;
  gap: 14px;
  align-items: stretch;
}

.songRecommendVideoThumbWrap {
  position: relative;
  flex-shrink: 0;
  width: 160px;
  min-width: 160px;
}

.songRecommendVideoThumb {
  width: 100%;
  height: 90px;
  object-fit: cover;
  display: block;
}

.songRecommendPreviewBadge {
  position: absolute;
  bottom: 6px;
  left: 6px;
  font-size: 10px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  background: rgba(0, 0, 0, 0.7);
  padding: 2px 6px;
  border-radius: 4px;
}

.songRecommendPreviewInfo {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  padding: 12px 14px 12px 0;
}

.songRecommendPreviewTitle {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-family: "NanumSquareRoundB", sans-serif;
}

.songRecommendPreviewMeta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.songRecommendChannelPreview {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.songRecommendChannelImg {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.songRecommendChannelFallback {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: linear-gradient(135deg, #66bdff 0%, #a8d8ff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}

.songRecommendChannelName {
  font-size: 12px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: "NanumSquareRoundR", sans-serif;
}

.songRecommendPreviewDate {
  font-size: 11px;
  color: #999;
  flex-shrink: 0;
  font-family: "NanumSquareRoundR", sans-serif;
}

.noticeIcon {
  font-size: 1.2rem;
  flex-shrink: 0;
}

.noticeText {
  font-size: 0.85rem;
  color: #856404;
  line-height: 1.5;
  font-family: "NanumSquareRoundR", sans-serif;
}

/* 제목 섹션 */
.titleSection {
  padding: 25px;
  border-bottom: 1px solid #f0f0f0;
}

.titleInput {
  width: 100%;
  border: none;
  outline: none;
  font-size: 22px;
  font-weight: 600;
  color: #333;
  background: transparent;
  font-family: "NanumSquareRoundB", sans-serif;
  padding: 0;
}

.titleInput::placeholder {
  color: #999;
  font-weight: 400;
}

/* 내용 섹션 */
.contentSection {
  flex: 1;
  padding: 25px;
  display: flex;
  min-height: 65vh;
  flex-direction: column;
}

.contentTextarea {
  width: 100%;
  flex: 1;
  border: none;
  outline: none;
  font-size: 15px;
  line-height: 1.6;
  color: #333;
  background: transparent;
  font-family: "NanumSquareRoundR", sans-serif;
  resize: none;
  padding: 0;
  min-height: 350px;
}

.contentTextarea::placeholder {
  color: #999;
}

/* 이미지 업로드 섹션 */
.imageSection {
  padding: 18px 25px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
}

.imageSectionHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-family: "NanumSquareRoundR", sans-serif;
}

.imageSectionTitle {
  font-size: 13px;
  font-weight: 500;
  color: #666;
}

.imageUploadArea {
  margin-top: 10px;
}

.fileInput {
  display: none;
}

.uploadButton {
  background: #66bdff;
  color: white;
  border: none;
  padding: 7px 14px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.uploadButton:hover {
  background: #4a9eff;
  transform: translateY(-1px);
}

.uploadButton:active {
  transform: translateY(0);
}

/* 이미지 미리보기 그리드 */
.imagePreviewGrid {
  margin-top: 15px;
  border-radius: 12px;
  overflow: hidden;
  display: grid;
  gap: 8px;
}

/* 1개 이미지 */
.grid1 {
  grid-template-columns: 1fr;
}

.grid1 .imagePreviewItem {
  max-height: 200px;
}

/* 2개 이미지 - 가로로 배치 */
.grid2 {
  grid-template-columns: repeat(2, 1fr);
}

.grid2 .imagePreviewItem {
  height: 200px;
}

/* 3개 이미지 - 가로로 배치 */
.grid3 {
  grid-template-columns: repeat(3, 1fr);
}

.grid3 .imagePreviewItem {
  height: 200px;
}

/* 4개 이미지 - 2x2 그리드 */
.grid4 {
  grid-template-columns: repeat(2, 1fr);
}

.grid4 .imagePreviewItem {
  height: 200px;
}

.imagePreviewItem {
  position: relative;
  width: 100%;
  height: 120px;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
}

.previewImage {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.removeImageButton {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
  z-index: 10;
  transition: all 0.2s ease;
}

.removeImageButton:hover {
  background: #ff4444;
  transform: scale(1.1);
}

/* 하단 정보 */
.footer {
  padding: 18px 25px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
  border-radius: 0 0 8px 8px;
  display: flex;
  justify-content: flex-end;
}

.wordCount {
  color: #666;
  font-size: 13px;
  font-family: "NanumSquareRoundR", sans-serif;
}

.bandOffSection {
  padding: 18px 25px;
  border-bottom: 1px solid #f0f0f0;
  background: #f8fbff;
}

.bandOffTitle {
  margin: 0 0 12px 0;
  font-size: 16px;
  font-family: "NanumSquareRoundB", sans-serif;
}

.bandOffSubtitle {
  margin: 14px 0 8px;
  font-size: 13px;
  color: #334155;
  font-family: "NanumSquareRoundB", sans-serif;
}

.bandOffRow {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.bandOffLabel {
  width: 120px;
  font-size: 12px;
  color: #64748b;
}

.bandOffInput {
  flex: 1;
  border: 1px solid #dbe3ee;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 12px;
}

.partLimitGrid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}

.partLimitItem {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px;
}

.partLimitInput {
  width: 58px;
  border: 1px solid #dbe3ee;
  border-radius: 6px;
  padding: 4px 6px;
  font-size: 12px;
}

.songItem {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px;
  margin-bottom: 8px;
  background: #fff;
}

.songTitleInput {
  width: 100%;
  border: 1px solid #dbe3ee;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 12px;
}

.songPartSelect {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.songPartButton {
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #475569;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 11px;
}

.songPartButtonActive {
  background: #1d4ed8;
  color: #fff;
  border-color: #1d4ed8;
}

.songRemoveButton,
.songAddButton {
  margin-top: 8px;
  border: none;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 12px;
  color: #fff;
  background: #64748b;
}

.songAddButton {
  background: #3b82f6;
}

/* ========================================
   Small (~480px)
   ======================================== */
@media screen and (max-width: 480px) {
  .container {
    width: 100%;
  }

  .paper {
    min-height: calc(100vh - 90px);
  }

  .header {
    padding: 12px 15px;
  }

  .title {
    font-size: 16px;
  }

  .backButton {
    font-size: 14px;
    padding: 6px 10px;
  }

  .backButtonText {
    display: none;
  }

  .publishButton {
    padding: 8px 16px;
    font-size: 12px;
  }

  .categorySection {
    padding: 12px 15px;
  }

  .categoryLabel {
    font-size: 12px;
    margin-bottom: 8px;
  }

  .dropdownContainer {
    margin-bottom: 8px;
  }

  .customDropdown {
    padding: 10px 14px;
    font-size: 12px;
  }

  .dropdownArrow {
    font-size: 10px;
  }

  .dropdownOption {
    padding: 10px 14px;
    font-size: 12px;
  }

  .checkbox {
    width: 14px;
    height: 14px;
    margin-right: 6px;
  }

  .checkboxText {
    font-size: 12px;
  }

  .privateCheckboxText {
    font-size: 12px;
  }

  .privateHint {
    font-size: 10px;
    margin-left: 20px;
  }

  .optionalBadge {
    font-size: 10px;
    margin-left: 6px;
  }

  .permissionNotice,
  .performanceNotice {
    margin-top: 8px;
    padding: 10px 12px;
    gap: 8px;
  }

  .noticeIcon {
    font-size: 1rem;
  }

  .noticeText {
    font-size: 0.8rem;
  }

  .titleSection {
    padding: 15px;
  }

  .titleInput {
    font-size: 18px;
  }

  .contentSection {
    padding: 15px;
  }

  .contentTextarea {
    font-size: 14px;
    min-height: 250px;
    line-height: 1.5;
  }

  .imageSection {
    padding: 12px 15px;
  }

  .bandOffSection {
    padding: 12px 15px;
  }

  .bandOffRow {
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
  }

  .bandOffLabel {
    width: auto;
  }

  .partLimitGrid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .imageSectionHeader {
    margin-bottom: 10px;
  }

  .imageSectionTitle {
    font-size: 12px;
  }

  .imageUploadArea {
    margin-top: 8px;
  }

  .uploadButton {
    padding: 6px 12px;
    font-size: 12px;
  }

  .imagePreviewGrid {
    margin-top: 12px;
    gap: 2px;
  }

  .removeImageButton {
    top: 6px;
    right: 6px;
    width: 24px;
    height: 24px;
    font-size: 16px;
  }

  .footer {
    padding: 12px 15px;
  }

  .wordCount {
    font-size: 12px;
  }
}
</style>
